import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

let agentInstance = null;

export function initNewRelic() {
  const runtimeConfig = window.__APP_CONFIG__ || {};
  const licenseKey =
    runtimeConfig.VITE_NEW_RELIC_LICENSE_KEY || import.meta.env.VITE_NEW_RELIC_LICENSE_KEY;
  const applicationID = runtimeConfig.VITE_NEW_RELIC_APP_ID || import.meta.env.VITE_NEW_RELIC_APP_ID;
  const accountID =
    runtimeConfig.VITE_NEW_RELIC_ACCOUNT_ID || import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID;
  const trustKey =
    runtimeConfig.VITE_NEW_RELIC_TRUST_KEY || import.meta.env.VITE_NEW_RELIC_TRUST_KEY || accountID;
  const agentID =
    runtimeConfig.VITE_NEW_RELIC_AGENT_ID ||
    import.meta.env.VITE_NEW_RELIC_AGENT_ID ||
    applicationID;
  const beacon =
    runtimeConfig.VITE_NEW_RELIC_BEACON || import.meta.env.VITE_NEW_RELIC_BEACON || 'bam.nr-data.net';

  if (!licenseKey || !applicationID || !accountID) {
    console.info(
      '[New Relic] Browser agent not configured. Set VITE_NEW_RELIC_LICENSE_KEY, VITE_NEW_RELIC_APP_ID, and VITE_NEW_RELIC_ACCOUNT_ID to enable RUM.',
    );
    return null;
  }

  const options = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: [] },
      page_view_timing: { enabled: true },
      // SPA + session_trace let New Relic record SPA route changes as their own
      // page views (so dwell time per /items, /chaos, etc. is queryable) and
      // capture an interaction trace per session for time-on-page analysis.
      spa: { enabled: true },
      session_trace: { enabled: true },
      session_replay: { enabled: false },
    },
    info: {
      beacon,
      errorBeacon: beacon,
      licenseKey,
      applicationID,
      sa: 1,
    },
    loader_config: {
      accountID,
      trustKey,
      agentID,
      licenseKey,
      applicationID,
    },
  };

  try {
    agentInstance = new BrowserAgent(options);
    console.info('[New Relic] Browser agent initialized');
  } catch (err) {
    console.warn('[New Relic] Failed to initialize browser agent:', err);
  }

  return agentInstance;
}

export function addPageAction(name, attributes = {}) {
  if (agentInstance) {
    agentInstance.addPageAction(name, attributes);
  }
}

export function noticeError(error, customAttributes = {}) {
  if (agentInstance) {
    agentInstance.noticeError(error, customAttributes);
  }
}

export function setCustomAttribute(key, value) {
  if (agentInstance) {
    agentInstance.setCustomAttribute(key, value);
  }
}

export function interaction() {
  if (agentInstance) {
    return agentInstance.interaction();
  }
  return null;
}

// Tags every subsequent NR event in the session with the user's identity so
// that NR's Sessions list groups by person and EndUserId is searchable in NRQL
// (`FROM PageView SELECT * WHERE enduser.id = '...'`). Setting both setUserId
// (preferred, native NR field) and setCustomAttribute('user.email', ...)
// covers older agent versions and gives NRQL-friendly dimensions.
export function setUserIdentity(user) {
  if (!agentInstance || !user) {
    return;
  }
  if (typeof agentInstance.setUserId === 'function' && user.subject) {
    agentInstance.setUserId(user.subject);
  }
  if (user.email) {
    agentInstance.setCustomAttribute('user.email', user.email);
  }
  if (user.name) {
    agentInstance.setCustomAttribute('user.name', user.name);
  }
  if (user.subject) {
    agentInstance.setCustomAttribute('user.id', user.subject);
  }
  agentInstance.setCustomAttribute('user.authenticated', Boolean(user.authenticated));
}
